/**
 * Request and response schemas for org invites: create (single + bulk), token-based accept, and
 * revocation. `BACKLOG.md` O2.
 *
 * Same rationale as `authRequests.ts`, restated rather than assumed: a schema here is the single
 * source of truth for both server validation and the web app's inferred types, every object schema
 * is strict (no silently-ignored extra key), and no server-owned value — `orgId`, `invitedByUserId`,
 * `tokenHash`, any timestamp — appears in a request schema. The invite's `role` is the one field that
 * *is* caller-supplied despite being security-sensitive, and that is deliberate: choosing what role an
 * invitee receives is exactly what `org:manage_members` authorizes, checked by `requireCapability` at
 * the route, not by this schema. `roleSchema` still closes it to `@emr/shared`'s own `ROLES` union, so
 * a typo or an invented role name is a 400 rather than a row nothing in `authorization.ts` recognises.
 */

import { z } from 'zod';

import { ROLES } from './authorization.ts';
import { emailSchema, passwordSchema } from './authRequests.ts';

/** Any role a membership may hold — see `authorization.ts` for what each one grants. */
export const roleSchema = z.enum(ROLES);

/** One invite: who, and in what role. */
export const createInviteRequestSchema = z.strictObject({
  email: emailSchema,
  role: roleSchema,
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

/**
 * Upper bound on one bulk request.
 *
 * Each invite in the batch is its own row, its own audit event, and its own chain-shard append —
 * unbounded input here is an unbounded amount of work (and an unbounded transaction) per request, the
 * same reasoning `MAX_PASSWORD_LENGTH` gives for bounding a single field. 100 is comfortably above a
 * real practice's one-time roster import and far below anything that turns a request into a batch job.
 */
export const MAX_BULK_INVITES = 100;

export const bulkCreateInviteRequestSchema = z.strictObject({
  invites: z.array(createInviteRequestSchema).min(1).max(MAX_BULK_INVITES),
});
export type BulkCreateInviteRequest = z.infer<typeof bulkCreateInviteRequestSchema>;

/**
 * Accept an invite. Unauthenticated by definition — the invitee may not have an account yet — so the
 * token itself is the only proof of identity this call has, which is why it travels in the body
 * (never a query string; see `sessionTokens.ts`'s identical reasoning for why a bearer secret never
 * goes in a URL).
 *
 * `password` is optional at the schema layer because it is only *required* on one of the two accept
 * paths (a brand-new user) and irrelevant on the other (an existing user joining a second practice) —
 * that branch is a business decision the service makes, not something expressible as a static schema
 * shape. An existing user's own password is unrelated to and unauthenticated by an invite token, so
 * a `password` field on this request is never used to verify anything for that path, only to set one
 * for a user this call creates.
 */
export const acceptInviteRequestSchema = z.strictObject({
  token: z.string().min(1),
  password: passwordSchema.optional(),
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

/**
 * Non-fatal warnings a create carries back alongside an otherwise-successful invite. `BACKLOG.md`
 * T16: distinct from `INVITE_ERROR_CODES` on purpose — nothing here refuses the create, so this is
 * never the `error` field of a failure response, only an optional rider on a `201`.
 */
export const INVITE_WARNING_CODES = [
  /**
   * The invited role is `reseller_admin` and the org the invite is being created in is itself a
   * child org (`orgs.parentOrgId IS NOT NULL`). `BACKLOG.md` T16 treats `reseller_admin` as meant
   * for a *parent* org (an MSO/billing company) — the depth-constraint trigger in
   * `0025_org_hierarchy_read_access.sql` guarantees a child org can never itself gain children, so
   * `org:read_children`, the one capability this role holds beyond `patient:read`, is structurally
   * inert for a membership granted here: `resolveReachableOrgIds` will always resolve this session
   * to itself alone, the same as any other role. Not refused — a known, accepted simplification per
   * that row's own text, not a hard rule — just surfaced so the admin granting it isn't left to
   * discover the mismatch on their own.
   */
  'reseller_admin_in_child_org',
] as const;
export type InviteWarningCode = (typeof INVITE_WARNING_CODES)[number];

export function isInviteWarningCode(value: string): value is InviteWarningCode {
  return (INVITE_WARNING_CODES as readonly string[]).includes(value);
}

/** One invite, as returned to the admin who manages it. */
export const inviteResponseSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  email: z.string(),
  role: roleSchema,
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
  /**
   * The raw bearer token — present **only** in the response to the create call that just minted it,
   * the one moment it exists outside the hashed column. Every other response involving an invite
   * (bulk create's own per-row entries aside) omits it entirely; there is no endpoint that reads it
   * back. Optional here rather than a second near-identical schema, matching how `IssuedSession`
   * carries its token as a plain field rather than a wrapper type.
   */
  token: z.string().optional(),
  /**
   * Present only when `createInvite` (`server/src/invites/service.ts`) detected
   * `reseller_admin_in_child_org` at creation time — absent, not `null`, on every ordinary invite,
   * matching `token`'s own optional-not-nullable shape immediately above.
   */
  warning: z.enum(INVITE_WARNING_CODES).optional(),
});
export type InviteResponse = z.infer<typeof inviteResponseSchema>;

export const createInviteResponseSchema = z.strictObject({ invite: inviteResponseSchema });
export type CreateInviteResponse = z.infer<typeof createInviteResponseSchema>;

export const bulkCreateInviteResponseSchema = z.strictObject({
  invites: z.array(inviteResponseSchema),
});
export type BulkCreateInviteResponse = z.infer<typeof bulkCreateInviteResponseSchema>;

/**
 * The failure vocabulary for accepting an invite, as a closed union.
 *
 * Unlike `AUTH_ERROR_CODES`'s `invalid_credentials`, these are **not** folded into one code to avoid
 * an existence oracle. Login's oracle concern is that the caller supplies a *guessed* identifier
 * (an email address) and the response would confirm whether it names a real account. An invite token
 * is 256 bits of CSPRNG output the caller can only have by having actually received the invite (email,
 * out of band) — nothing here is guessable, so telling the holder of a genuine token *why* it no
 * longer works (expired vs. revoked vs. already used) discloses nothing an attacker could exploit and
 * is materially more useful to a real invitee working out what to do next.
 */
export const INVITE_ERROR_CODES = [
  /** The body failed schema validation. */
  'invalid_request',
  /** No invite matches this token — wrong, malformed, or never existed. One code; see above. */
  'invite_invalid',
  /** The invite's `expiresAt` has passed. */
  'invite_expired',
  /** An admin revoked this invite before it was accepted. */
  'invite_revoked',
  /** This invite has already been used to create a membership. Single-use, not idempotent. */
  'invite_already_accepted',
  /** The invited email has no existing account, and no password was supplied to create one. */
  'password_required',
  /** The invited email already belongs to a member of this org. */
  'already_member',
  /**
   * The invited email belongs to an existing account that is deactivated (workforce termination) or
   * deleted. Not folded into `invite_invalid` the way login folds an unknown/wrong-password/deactivated
   * account into one `invalid_credentials` code: login's oracle concern is that the caller supplies a
   * *guessed* identifier, but an invite-accept caller already knows the email exists — they hold an
   * admin-issued invite naming it — so a specific code discloses nothing an attacker could exploit and
   * is materially more useful to a real invitee working out what happened. See `auth/service.ts`'s
   * `login`, which refuses on the identical condition (`user.deactivatedAt`/`user.deletedAt`) for the
   * same HIPAA §164.308(a)(3)(ii)(C) reason: a terminated user must not gain a live session through any
   * path, including one that never touches a password.
   */
  'account_deactivated',
  /** Same as `AUTH_ERROR_CODES`'s member of the same name — a write that cannot be audited did not
   * happen. */
  'audit_unavailable',
] as const;
export type InviteErrorCode = (typeof INVITE_ERROR_CODES)[number];

export function isInviteErrorCode(value: string): value is InviteErrorCode {
  return (INVITE_ERROR_CODES as readonly string[]).includes(value);
}

export const inviteErrorResponseSchema = z.strictObject({
  error: z.enum(INVITE_ERROR_CODES),
});
export type InviteErrorResponse = z.infer<typeof inviteErrorResponseSchema>;
